import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PERMISSION_LABELS = {
  can_track_finance: "Log financial expenses",
  can_view_finance: "View expense ledger",
  can_generate_reports: "Generate reports",
  can_view_all_tasks: "See all team tasks",
  can_assign_tasks: "Assign tasks to others",
};

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/roles").then(r => setRoles(r.data)).catch(()=>{});
    api.get("/roles/permissions").then(r => setPermissions(r.data.permissions || [])).catch(()=>{});
  };
  useEffect(load, []);

  const onSaved = () => { setOpen(false); setEditing(null); load(); };

  const remove = async (role_id) => {
    try {
      await api.delete(`/roles/${role_id}`);
      toast.success("Role removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout
      title="Roles"
      subtitle="Custom permissions"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-10 px-4 rounded-xl btn-warm text-[13px] font-semibold" data-testid="role-create-button">
              <Plus className="h-4 w-4 mr-1.5" /> New role
            </Button>
          </DialogTrigger>
          <RoleDialog permissions={permissions} role={null} onSaved={onSaved} />
        </Dialog>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map(r => (
          <div key={r.role_id} className="tech-card p-5 rise" data-testid={`role-card-${r.role_id}`}>
            <div className="flex items-start justify-between">
              <div className="h-9 w-9 rounded-md bg-neutral-100 grid place-items-center">
                <ShieldCheck className="h-4 w-4 text-neutral-700" strokeWidth={1.6} />
              </div>
              {r.is_builtin && <span className="overline text-[10px]">Built-in</span>}
            </div>
            <h3 className="font-display text-xl font-semibold mt-4 leading-tight">{r.name}</h3>
            <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{r.description || "—"}</p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {Object.entries(r.permissions || {}).filter(([_, v]) => v).map(([k]) => (
                <span key={k} className="text-[10px] uppercase tracking-wider border border-neutral-200 rounded-sm px-1.5 py-0.5 text-neutral-700">
                  {PERMISSION_LABELS[k] || k}
                </span>
              ))}
            </div>
            {!r.is_builtin && (
              <div className="mt-5 flex gap-2">
                <Dialog open={editing?.role_id === r.role_id} onOpenChange={(o) => setEditing(o ? r : null)}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid={`role-edit-${r.role_id}`}>Edit</Button>
                  </DialogTrigger>
                  {editing?.role_id === r.role_id && <RoleDialog role={r} permissions={permissions} onSaved={onSaved} />}
                </Dialog>
                <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => remove(r.role_id)} data-testid={`role-delete-${r.role_id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Layout>
  );
}

function RoleDialog({ role, permissions, onSaved }) {
  const [form, setForm] = useState({
    name: role?.name || "",
    description: role?.description || "",
    permissions: role?.permissions || {},
  });
  const [submitting, setSubmitting] = useState(false);

  const togglePerm = (k) => {
    setForm(f => ({ ...f, permissions: { ...f.permissions, [k]: !f.permissions[k] } }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSubmitting(true);
    try {
      if (role) {
        await api.patch(`/roles/${role.role_id}`, form);
        toast.success("Role updated");
      } else {
        await api.post("/roles", form);
        toast.success("Role created");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">{role ? "Edit role" : "Create role"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div className="space-y-1.5">
          <Label className="overline text-[10px]">Name</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="role-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-[10px]">Description</Label>
          <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="role-description-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-[10px]">Permissions</Label>
          <div className="border border-neutral-200 rounded-md divide-y divide-neutral-200">
            {permissions.map(p => (
              <div key={p} className="flex items-center justify-between p-3">
                <div>
                  <div className="text-sm font-medium">{PERMISSION_LABELS[p] || p}</div>
                  <div className="text-xs text-neutral-500 font-mono">{p}</div>
                </div>
                <Switch checked={!!form.permissions[p]} onCheckedChange={() => togglePerm(p)} data-testid={`perm-${p}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={submitting} className="bg-neutral-950 hover:bg-neutral-800 text-white" data-testid="role-submit-button">
          {submitting ? "Saving…" : "Save role"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
